import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import { ArrowDown, ArrowUp, Eye, PlusCircle, Trash } from 'react-bootstrap-icons';
import { useHistory, useParams } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import Loader from '../Loader';
import CreatableDropdown from '../common/CreatableDropdown';
import HorizontalProgress from '../common/HorizontalProgress';
import ConversationBankFlow from '../conversation-bank/ConversationBankFlow';
import '../conversation-bank/conversationBank.scss';

const emptyRecord = () => ({
  status: 'Active',
  topicCategory: '',
  scenarioName: '',
  customerQuestionSituation: '',
  searchTerms: [],
  startingResponse: '',
  pitchReadySignal: '',
  customerSummary: '',
  pitchBridge: '',
  relevantPitch: '',
  appointmentInvitation: '',
  alternativeNextStep: '',
  confirmationLanguage: '',
  whatNotToDo: '',
  branches: []
});

const emptyBranch = () => ({
  branchOrder: 0,
  status: 'Active',
  possibleCustomerReply: '',
  recommendedResponse: ''
});

const compactSelectStyles = {
  control: base => ({ ...base, minHeight: 31, fontSize: 14 }),
  valueContainer: base => ({ ...base, padding: '0 8px' }),
  indicatorsContainer: base => ({ ...base, height: 31 }),
  menu: base => ({ ...base, fontSize: 14, zIndex: 5 })
};

const Field = ({ label, as = 'textarea', rows = 2, value, onChange, required, placeholder }) => (
  <Form.Group className="mb-0">
    {label ? (
      <Form.Label className="small mb-1">
        {label}
        {required ? ' *' : ''}
      </Form.Label>
    ) : null}
    <Form.Control
      size="sm"
      as={as === 'input' ? 'input' : as}
      rows={as === 'textarea' ? rows : undefined}
      value={value || ''}
      placeholder={placeholder}
      onChange={onChange}
    />
  </Form.Group>
);

const normalizeSearchTerms = value => {
  const source = Array.isArray(value) ? value : String(value || '').split(',');
  const terms = [];
  source.forEach(item => {
    String(item || '')
      .split(',')
      .map(term => term.trim())
      .filter(Boolean)
      .forEach(term => {
        if (!terms.includes(term)) terms.push(term);
      });
  });
  return terms;
};

const SearchTermsField = ({ value, onChange }) => {
  const terms = normalizeSearchTerms(value);
  const selected = terms.map(term => ({ label: term, value: term }));
  return (
    <Form.Group className="mb-0">
      <Form.Label className="small mb-1">Search terms</Form.Label>
      <CreatableSelect
        isMulti
        options={selected}
        value={selected}
        onChange={items => onChange(normalizeSearchTerms((items || []).map(item => item.value)))}
        onCreateOption={input => onChange(normalizeSearchTerms([...terms, input]))}
        placeholder="Type a term and press Enter"
        formatCreateLabel={input => `Add "${input}"`}
        styles={compactSelectStyles}
        classNamePrefix="creatable-select"
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
    </Form.Group>
  );
};

const FormSection = ({ step, title, note, tone, children, action }) => (
  <Card className={`mb-2 ${tone === 'internal' ? 'cb-guidance' : ''} ${tone === 'danger' ? 'cb-dont' : ''}`}>
    <Card.Body className="py-2 px-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="small font-weight-bold mb-0">
          {step}. {title}
          {note ? <span className="font-weight-normal text-muted ml-2">{note}</span> : null}
        </div>
        {action}
      </div>
      {children}
    </Card.Body>
  </Card>
);

const ConversationBankEditor = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const history = useHistory();
  const [form, setForm] = useState(emptyRecord());
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [categories, setCategories] = useState([]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  useEffect(() => {
    const loadCategories = async () => {
      const { response } = await makeApiRequests({
        endpoint: `${ENDPOINTS.CONVERSATION_BANK_FILTERS}?includeInactive=true`,
        method: 'GET'
      });
      setCategories(response?.categories || []);
    };
    loadCategories();
  }, []);

  useEffect(() => {
    if (isNew) return;
    const load = async () => {
      setLoading(true);
      const { response, error } = await makeApiRequests({
        endpoint: `${ENDPOINTS.CONVERSATION_BANK_WITH_ID(id)}?includeInactive=true`,
        method: 'GET'
      });
      setLoading(false);
      if (error) {
        toast.error(error);
        return;
      }
      setForm({
        ...emptyRecord(),
        ...response,
        searchTerms: normalizeSearchTerms(response.searchTerms),
        branches: response.branches || []
      });
    };
    load();
  }, [isNew, id]);

  const updateBranch = (index, patch) => {
    setForm(prev => {
      const branches = [...(prev.branches || [])];
      branches[index] = { ...branches[index], ...patch };
      return { ...prev, branches };
    });
  };

  const moveBranch = (index, direction) => {
    setForm(prev => {
      const branches = [...(prev.branches || [])];
      const next = index + direction;
      if (next < 0 || next >= branches.length) return prev;
      const swap = branches[index];
      branches[index] = branches[next];
      branches[next] = swap;
      return {
        ...prev,
        branches: branches.map((branch, i) => ({ ...branch, branchOrder: i + 1 }))
      };
    });
  };

  const removeBranch = index => {
    setForm(prev => ({
      ...prev,
      branches: prev.branches.filter((_, i) => i !== index).map((branch, i) => ({ ...branch, branchOrder: i + 1 }))
    }));
  };

  const addBranch = () => {
    setForm(prev => ({
      ...prev,
      branches: [...(prev.branches || []), { ...emptyBranch(), branchOrder: (prev.branches || []).length + 1 }]
    }));
  };

  const payload = () => ({
    status: form.status,
    topicCategory: form.topicCategory,
    scenarioName: form.scenarioName,
    customerQuestionSituation: form.customerQuestionSituation,
    searchTerms: normalizeSearchTerms(form.searchTerms),
    startingResponse: form.startingResponse,
    pitchReadySignal: form.pitchReadySignal,
    customerSummary: form.customerSummary,
    pitchBridge: form.pitchBridge,
    relevantPitch: form.relevantPitch,
    appointmentInvitation: form.appointmentInvitation,
    alternativeNextStep: form.alternativeNextStep,
    confirmationLanguage: form.confirmationLanguage,
    whatNotToDo: form.whatNotToDo,
    branches: (form.branches || []).map((branch, index) => ({
      _id: branch._id || undefined,
      branchOrder: index + 1,
      status: branch.status || 'Active',
      possibleCustomerReply: branch.possibleCustomerReply,
      recommendedResponse: branch.recommendedResponse
    }))
  });

  const onSave = async () => {
    if (!form.topicCategory || !form.scenarioName || !form.customerQuestionSituation || !form.startingResponse) {
      toast.error('Topic, scenario name, customer question, and starting response are required.');
      return;
    }
    setSaving(true);
    const { response, error } = await makeApiRequests({
      endpoint: isNew ? ENDPOINTS.CONVERSATION_BANK_BASE : ENDPOINTS.CONVERSATION_BANK_WITH_ID(id),
      method: isNew ? 'POST' : 'PUT',
      requestBody: payload()
    });
    setSaving(false);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success('Conversation saved');
    if (isNew && response._id) {
      history.replace(`/admin/conversation-bank/${response._id}`);
    } else {
      setForm({
        ...emptyRecord(),
        ...response,
        searchTerms: normalizeSearchTerms(response.searchTerms),
        branches: response.branches || []
      });
    }
  };

  const onCategoryChange = value => {
    setField('topicCategory', value);
    if (value && !categories.includes(value)) {
      setCategories(prev => [...prev, value].sort((a, b) => a.localeCompare(b)));
    }
  };

  if (loading) return <Loader />;

  const categoryOptions =
    form.topicCategory && !categories.includes(form.topicCategory)
      ? [...categories, form.topicCategory]
      : categories;

  const updatedBy = form.updatedBy?.name || form.createdBy?.name;
  const updatedAt = form.updatedAt || form.createdAt;

  return (
    <div className="container py-4 cb-admin-form" style={{ minHeight: 'calc(100vh - 56px)' }}>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
        <div>
          <Button variant="link" className="px-0 py-0" onClick={() => history.push('/admin/conversation-bank')}>
            Back to list
          </Button>
          <h5 className="mb-0">{isNew ? 'Add conversation' : form.scenarioName || 'Edit conversation'}</h5>
          {!isNew && (
            <div className="tinyFont text-muted">
              Last updated {updatedBy || 'Unknown'}
              {updatedAt ? ` · ${new Date(updatedAt).toLocaleString()}` : ''}
            </div>
          )}
        </div>
        <div className="mt-2 mt-md-0">
          <Button size="sm" variant="outline-info" className="mr-2" onClick={() => setShowPreview(true)}>
            <Eye className="mr-1" />
            Preview
          </Button>
          <Button size="sm" onClick={onSave} disabled={saving}>
            Save
          </Button>
        </div>
      </div>
      {saving && <HorizontalProgress text="Saving conversation..." />}

      <Card className="mb-2">
        <Card.Body className="py-2 px-3">
          <Row>
            <Col md={5} className="mb-2 mb-md-0">
              <Field
                label="Scenario name"
                as="input"
                required
                value={form.scenarioName}
                onChange={e => setField('scenarioName', e.target.value)}
              />
            </Col>
            <Col md={4} className="mb-2 mb-md-0">
              <Form.Group className="mb-0">
                <Form.Label className="small mb-1">Topic / category *</Form.Label>
                <CreatableDropdown
                  options={categoryOptions}
                  value={form.topicCategory}
                  onChange={onCategoryChange}
                  required
                  placeholder="Select or add a category"
                  styles={compactSelectStyles}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-0">
                <Form.Label className="small mb-1">Status</Form.Label>
                <Form.Control
                  size="sm"
                  as="select"
                  value={form.status}
                  onChange={e => setField('status', e.target.value)}
                >
                  <option>Active</option>
                  <option>Inactive</option>
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
          <div className="mt-2">
            <SearchTermsField value={form.searchTerms} onChange={terms => setField('searchTerms', terms)} />
          </div>
        </Card.Body>
      </Card>

      <FormSection step="1" title="Customer question or situation">
        <Field
          label="What the customer says"
          required
          value={form.customerQuestionSituation}
          onChange={e => setField('customerQuestionSituation', e.target.value)}
        />
      </FormSection>

      <FormSection step="2" title="Starting response">
        <Field
          label="Opening reply"
          required
          value={form.startingResponse}
          onChange={e => setField('startingResponse', e.target.value)}
        />
      </FormSection>

      <FormSection
        step="3"
        title="Possible customer replies"
        action={
          <Button size="sm" variant="outline-dark" onClick={addBranch}>
            <PlusCircle className="mr-1" />
            Add branch
          </Button>
        }
      >
        {(form.branches || []).length === 0 && <div className="text-muted small">No reply branches yet.</div>}
        {(form.branches || []).map((branch, index) => (
          <div
            key={branch._id || `new-${index}`}
            className={`rounded-border p-3 ${index === form.branches.length - 1 ? 'mb-0' : 'mb-3'}`}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center">
                <Badge variant="info">CONTINUE · Branch {index + 1}</Badge>
                <Form.Control
                  size="sm"
                  as="select"
                  className="ml-2"
                  style={{ width: 110 }}
                  value={branch.status || 'Active'}
                  onChange={e => updateBranch(index, { status: e.target.value })}
                >
                  <option>Active</option>
                  <option>Inactive</option>
                </Form.Control>
              </div>
              <div>
                <Button size="sm" variant="light" className="mr-1 py-0" onClick={() => moveBranch(index, -1)}>
                  <ArrowUp />
                </Button>
                <Button size="sm" variant="light" className="mr-1 py-0" onClick={() => moveBranch(index, 1)}>
                  <ArrowDown />
                </Button>
                <Button size="sm" variant="outline-danger" className="py-0" onClick={() => removeBranch(index)}>
                  <Trash />
                </Button>
              </div>
            </div>
            <div className="smallFont text-muted mb-1">Possible customer reply</div>
            <Field
              label=""
              value={branch.possibleCustomerReply}
              onChange={e => updateBranch(index, { possibleCustomerReply: e.target.value })}
            />
            <div className="smallFont text-muted mb-1 mt-2">Recommended next response</div>
            <Field
              label=""
              value={branch.recommendedResponse}
              onChange={e => updateBranch(index, { recommendedResponse: e.target.value })}
            />
          </div>
        ))}
      </FormSection>

      <FormSection step="4" title="Pitch-ready signal" note="Internal" tone="internal">
        <Field
          label="What to understand before the pitch"
          value={form.pitchReadySignal}
          onChange={e => setField('pitchReadySignal', e.target.value)}
        />
      </FormSection>

      <FormSection step="5" title="Customer summary">
        <Field
          label="Copy-ready summary"
          value={form.customerSummary}
          onChange={e => setField('customerSummary', e.target.value)}
        />
      </FormSection>

      <FormSection step="6" title="Bridge to pitch">
        <Field label="Transition" value={form.pitchBridge} onChange={e => setField('pitchBridge', e.target.value)} />
      </FormSection>

      <FormSection step="7" title="Relevant pitch">
        <Field
          label="Buyer-specific recommendation"
          value={form.relevantPitch}
          onChange={e => setField('relevantPitch', e.target.value)}
        />
      </FormSection>

      <FormSection step="8" title="Appointment invitation">
        <Field
          label="Invitation"
          value={form.appointmentInvitation}
          onChange={e => setField('appointmentInvitation', e.target.value)}
        />
      </FormSection>

      <FormSection step="9" title="Alternative next step">
        <Field
          label="When they are not ready to visit"
          value={form.alternativeNextStep}
          onChange={e => setField('alternativeNextStep', e.target.value)}
        />
      </FormSection>

      <FormSection step="10" title="Confirmation">
        <Field
          label="Confirmation language"
          value={form.confirmationLanguage}
          onChange={e => setField('confirmationLanguage', e.target.value)}
        />
      </FormSection>

      <FormSection step="11" title="What not to do" note="Internal" tone="danger">
        <Field
          label="Do not say"
          value={form.whatNotToDo}
          onChange={e => setField('whatNotToDo', e.target.value)}
        />
      </FormSection>

      <div className="mt-3 mb-4">
        <Button size="sm" onClick={onSave} disabled={saving} className="mr-2">
          Save conversation
        </Button>
        <Button size="sm" variant="outline-dark" onClick={() => history.push('/admin/conversation-bank')}>
          Cancel
        </Button>
      </div>

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="h6 mb-0">Salesperson preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ConversationBankFlow
            record={{
              ...form,
              branches: form.branches || []
            }}
            placeholderContext={{}}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ConversationBankEditor;
